import React, { useState, useEffect, useCallback } from 'react';
import { filingApi, FilingTransaction, TransactionPage } from '../../../services/api';
import { fmtDate, fmtUSD2 as fmt2, fmtPct } from '../../../utils/fmt';

export type MetricKey = 'gross_sales' | 'exempt_sales' | 'taxable_sales' | 'tax_collected' | 'tax_due';

const TX = { date: '0 0 100px', order: '0 0 130px', channel: '0 0 100px', product: 1, gross: '0 0 100px', taxable: '0 0 100px', rate: '0 0 70px', tax: '0 0 90px', flag: '0 0 60px' };
const FILTER_LABELS: Record<string, string> = { gross_sales: 'All transactions', exempt_sales: 'Exempt transactions', taxable_sales: 'Taxable transactions', tax_collected: 'Tax collected', tax_due: 'Tax due' };
const TH: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' };

function SkTxRow(): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #F1F5F9' }}>
      {[100, 130, 100, 0, 100, 100, 70, 90, 60].map((w, i) => (
        <div key={i} className="skeleton-box" style={{ flex: w ? `0 0 ${w}px` : 1, height: 11 }} />
      ))}
    </div>
  );
}

export interface TransactionPanelProps {
  organizationId: string;
  stateCode: string;
  period: string;
  filter: MetricKey;
  onClose: () => void;
  flaggedIds: Set<string>;
  onFlag: (tx: FilingTransaction) => void;
}

export function TransactionPanel({ organizationId, stateCode, period, filter, onClose, flaggedIds, onFlag }: TransactionPanelProps): JSX.Element {
  const [page,    setPage]    = useState(1);
  const [data,    setData]    = useState<TransactionPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const load = useCallback((p: number): void => {
    setLoading(true);
    setError(null);
    filingApi.getTransactions(organizationId, stateCode, period, p, filter)
      .then(d => { setData(d); setPage(p); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId, stateCode, period, filter]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '11px 16px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{FILTER_LABELS[filter] ?? 'Transactions'}</span>
          {data && <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 8 }}>{data.total.toLocaleString()} transactions · 50 per page</span>}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4, lineHeight: 1 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div style={{ display: 'flex', background: '#F8FAFC', padding: '7px 16px', borderBottom: '1px solid #E2E8F0' }}>
        <span style={{ flex: TX.date,    ...TH }}>Date</span>
        <span style={{ flex: TX.order,   ...TH }}>Order ID</span>
        <span style={{ flex: TX.channel, ...TH }}>Channel</span>
        <span style={{ flex: TX.product, ...TH }}>Product</span>
        <span style={{ flex: TX.gross,   textAlign: 'right', ...TH }}>Gross</span>
        <span style={{ flex: TX.taxable, textAlign: 'right', ...TH }}>Taxable</span>
        <span style={{ flex: TX.rate,    textAlign: 'right', ...TH }}>Rate</span>
        <span style={{ flex: TX.tax,     textAlign: 'right', ...TH }}>Tax</span>
        <span style={{ flex: TX.flag,    ...TH }} />
      </div>
      {error ? (
        <div style={{ padding: '20px 16px', fontSize: 12, color: '#BE123C', textAlign: 'center' }}>
          {error} —{' '}
          <button onClick={() => load(page)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B4F8C', fontWeight: 600, fontSize: 12, padding: 0 }}>Retry</button>
        </div>
      ) : loading ? (
        Array.from({ length: 8 }).map((_, i) => <SkTxRow key={i} />)
      ) : data?.items.length === 0 ? (
        <div style={{ padding: '20px 16px', fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>No transactions match this filter.</div>
      ) : (data?.items ?? []).map((tx: FilingTransaction, i: number) => {
        const isFlagged = flaggedIds.has(tx.transaction_id);
        return (
          <div key={tx.transaction_id} style={{ display: 'flex', alignItems: 'center', padding: '9px 16px', fontSize: 12, borderBottom: '1px solid #F1F5F9', background: isFlagged ? '#FFF7ED' : i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
            <span style={{ flex: TX.date,    color: '#64748B' }}>{fmtDate(tx.transaction_date)}</span>
            <span style={{ flex: TX.order,   color: '#475569', fontFamily: 'monospace', fontSize: 11 }}>{tx.order_id}</span>
            <span style={{ flex: TX.channel, color: '#64748B' }}>{tx.channel}</span>
            <span style={{ flex: TX.product, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>{tx.product_name}</span>
            <span style={{ flex: TX.gross,   textAlign: 'right', color: '#475569' }}>{fmt2(tx.gross_amount)}</span>
            <span style={{ flex: TX.taxable, textAlign: 'right', color: '#475569' }}>{fmt2(tx.taxable_amount)}</span>
            <span style={{ flex: TX.rate,    textAlign: 'right', color: '#64748B', fontSize: 11 }}>{fmtPct(tx.tax_rate)}</span>
            <span style={{ flex: TX.tax,     textAlign: 'right', fontWeight: 600, color: '#0F172A' }}>{fmt2(tx.tax_amount)}</span>
            <div style={{ flex: TX.flag, display: 'flex', justifyContent: 'flex-end' }}>
              {isFlagged ? (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#C2410C', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 4, padding: '2px 6px' }}>Flagged</span>
              ) : (
                <button
                  onClick={() => onFlag(tx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#94A3B8', padding: '2px 4px', borderRadius: 4 }}
                  title="Flag this transaction for amendment"
                  onMouseEnter={e => { e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.background = '#FFF1F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.background = 'none'; }}
                >
                  Flag
                </button>
              )}
            </div>
          </div>
        );
      })}
      {data && data.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
          <button onClick={() => load(page - 1)} disabled={page <= 1} style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid #E2E8F0', cursor: page <= 1 ? 'default' : 'pointer', background: page <= 1 ? '#F8FAFC' : '#fff', color: page <= 1 ? '#CBD5E1' : '#0F172A' }}>← Prev</button>
          <span style={{ fontSize: 12, color: '#64748B' }}>Page {page} of {data.pages}</span>
          <button onClick={() => load(page + 1)} disabled={page >= data.pages} style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid #E2E8F0', cursor: page >= data.pages ? 'default' : 'pointer', background: page >= data.pages ? '#F8FAFC' : '#fff', color: page >= data.pages ? '#CBD5E1' : '#0F172A' }}>Next →</button>
        </div>
      )}
    </div>
  );
}
